import { observer } from "mobx-react";
import * as React from "react";
import { NodeCollectionStore, NodeStore, StoreType } from "../../../stores";
import "./TopBar.scss";
import { linkStore } from "../../../stores/LinkStore";
import { HALF, LINK_ID_LENGTH, ORIGIN } from "../../../Constants";
import { panningMath } from "../../../Utils";

let activeLinkSource: NodeStore | null = null;

interface TopBarProps {
    store: NodeStore;
}

@observer
export class TopBar extends React.Component<TopBarProps> {
    private isPointerDown = false;
    private showLinked = false;

    onPointerDown = (event: React.PointerEvent): void => {
        if ((event.target as HTMLElement).closest("button")) return;

        event.stopPropagation();
        event.preventDefault();
        this.isPointerDown = true;
        document.body.classList.add("dragging-node");

        document.addEventListener("pointermove", this.onPointerMove);
        document.addEventListener("pointerup", this.onPointerUp);
    };

    onPointerMove = (event: PointerEvent): void => {
        if (!this.isPointerDown) return;

        panningMath(this.props.store, event.movementX, event.movementY);
    };

    onPointerUp = (): void => {
        this.isPointerDown = false;
        document.body.classList.remove("dragging-node");
        
        document.removeEventListener("pointermove", this.onPointerMove);
        document.removeEventListener("pointerup", this.onPointerUp);

        this.tryReparent();
    };

    tryReparent() {
        const node = this.props.store;
        const parent = node.parent;
        if (!parent) return;

        const collectionX = node.x + node.width * HALF;
        const collectionY = node.y + node.height * HALF;

        for (const sibling of parent.nodes) {
            if (sibling.type !== StoreType.Collection || sibling === node) continue;

            const collection = sibling as NodeCollectionStore;

            if (
                collectionX >= collection.x &&
                collectionX <= collection.x + collection.width &&
                collectionY >= collection.y &&
                collectionY <= collection.y + collection.height
            ) {
                node.x -= collection.x;
                node.y -= collection.y;
                parent.removeNode(node);
                collection.addNodes([node]);
                return;
            }
        }
    }

    handleLinkClick = (event: React.MouseEvent) => {
        event.stopPropagation();

        const node = this.props.store;

        if (node.type === StoreType.Collection) return;

        if (!activeLinkSource) {
            activeLinkSource = node;
            return;
        }

        if (activeLinkSource === node) {
            activeLinkSource = null;
            return;
        }

        if (!activeLinkSource.links.includes(node.Id)) {
            activeLinkSource.links.push(node.Id);
        }

        if (!node.links.includes(activeLinkSource.Id)) {
            node.links.push(activeLinkSource.Id);
        }

        activeLinkSource = null;
    };

    toggleLinkedList = (event: React.MouseEvent) => {
        event.stopPropagation();
        this.showLinked = !this.showLinked;
        this.forceUpdate();
    };

    render() {
        const { store } = this.props;
        const isLinkSource = activeLinkSource === store;
        const isCollection = store.type === StoreType.Collection;

        return (
            <div
                className="topbar"
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
                onPointerDown={this.onPointerDown}
            >
                {isCollection && (
                    <button
                        onClick={(event) => {
                            event.stopPropagation();
                            const collection = store as NodeCollectionStore;
                            if (!collection.isGridView) {
                            collection.isGridView = true;
                            collection.applyGridSizing();
                                } else {
                                collection.isGridView = false;
                                collection.restoreFreeformSizing();
                            }
                        }}
                    >
                        {(store as NodeCollectionStore).isGridView ? "Freeform" : "Grid"}
                    </button>
                )}

            <div className="node-title">
                {store.Id.slice(ORIGIN, LINK_ID_LENGTH)}
                    </div>

                <div className="topbar-actions">
                    
                    {!isCollection && (
                        <>
                            <button
                                className={isLinkSource ? "active-link" : ""}
                                onClick={this.handleLinkClick}
                            >
                                Link
                            </button>

                            {store.links.length > ORIGIN && (
                                <button onClick={this.toggleLinkedList}>
                                    Linked
                                </button>
                            )}
                        </>
                    )}

                    {store.parent && (
                        <button
                            className="remove-button"
                            onClick={(event) => {
                                event.stopPropagation();
                                store.parent!.removeNode(store);
                            }}
                        >
                            X
                        </button>
                    )}
                </div>

                {this.showLinked && store.links.length > ORIGIN && (
    <div className="linked-list">
        {store.links.map((id) => (
            <div
                key={id}
                className="linked-item"
                onClick={(event) => {
                    event.stopPropagation();

                    const parent = store.parent;
                    if (!parent) return;

                    const root = parent.getRoot();
                    const target = root.getNodeById(id);
                    if (!target) return;

                    root.x = -target.x + window.innerWidth * HALF - target.width * HALF;
                    root.y = -target.y + window.innerHeight * HALF - target.height * HALF;
                }}
            >
                {id.slice(ORIGIN, LINK_ID_LENGTH)}
            </div>
        ))}
    </div>
)}
            </div>
        );
    }
}